import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { RefreshCw, Table as TableIcon, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../utils/api';
import { getSemanaIso } from '../utils/dateFormat';

interface AnalysisMatrixProps {
  comparisonProducts: any[];
  shouldLoad?: boolean;
  priceHistoryData?: any; // Datos precargados del componente padre
  loading?: boolean; // Estado de carga del componente padre
}

interface PriceHistoryPoint {
  fecha: string;
  tomaId: number;
  tableName: string;
  data: any;
}

const AnalysisMatrix: React.FC<AnalysisMatrixProps> = ({ comparisonProducts, shouldLoad = true, priceHistoryData, loading: parentLoading }) => {
  const { t } = useTranslation();
  const [localLoading, setLocalLoading] = useState(false);
  
  // Usar loading del padre si está disponible, sino usar el local
  const loading = parentLoading !== undefined ? parentLoading : localLoading;
  const [priceData, setPriceData] = useState<{[productId: string]: PriceHistoryPoint[]}>({});
  const [tomas, setTomas] = useState<{fecha: string, tomaId: number, tableName: string}[]>([]);
  const [, setHasLoadedData] = useState(false);
  const [hoveredCell, setHoveredCell] = useState<{productId: string, tomaId: number} | null>(null);
  const cellRefs = React.useRef<{[key: string]: React.RefObject<HTMLTableCellElement>}>({});

  const loadPriceHistory = async () => {
    if (comparisonProducts.length === 0) {
      setPriceData({});
      setTomas([]);
      return;
    }

    setLocalLoading(true);
    try {
      const allHistories: {[productId: string]: PriceHistoryPoint[]} = {};
      const allTomasMap = new Map<number, {fecha: string, tomaId: number, tableName: string}>();

      // Obtener el historial de precios para cada producto
      for (const product of comparisonProducts) {
        let productId = product.id_catalogo || product.ID_CATALOGO || product.id || product.comparisonId;
        const originalProductId = productId;
        
        if (productId) {
          try {
            // Si el producto tiene buybox_id, primero obtener la variante ganadora
            // TEMPORALMENTE DESHABILITADO PARA DEBUGGING
            /*
            if (product.buybox_id && product.buybox_id !== null && 
                product.buybox_id !== '(NULL)' && product.buybox_id !== 'NULL' && 
                product.buybox_id !== '0' && product.buybox_id !== 0) {
              
              
              try {
                // Buscar las variantes del buybox
                const variantsResponse = await api.get(`/api/buybox/${product.buybox_id}/variants`);
                const variantsData = variantsResponse.data;
                
                if (variantsData.variants && variantsData.variants.length > 0) {
                  // Encontrar la variante ganadora
                  const winnerVariant = variantsData.variants.find((v: any) => 
                    v.buybox_winner === 1 || v.buybox_winner === '1' || v.buybox_winner === true
                  );
                  
                  if (winnerVariant && winnerVariant.id_catalogo) {
                    // Usar el id_catalogo de la variante ganadora
                    productId = String(winnerVariant.id_catalogo);
                  }
                }
              } catch (variantsError) {
                console.error('AnalysisMatrix - Error fetching buybox variants:', variantsError);
                // Continuar con el productId original si falla
              }
            }
            */
            
            const response = await api.get(`/api/product/${encodeURIComponent(productId)}/price-history`);
            const historyData = response.data.history || response.data;
            const history: PriceHistoryPoint[] = Array.isArray(historyData) ? historyData : [];
            
            if (history.length > 0) {
              // Usar el originalProductId como clave para mantener consistencia
              allHistories[originalProductId] = history;
              
              // Collect all unique tomas
              history.forEach(point => {
                if (!allTomasMap.has(point.tomaId)) {
                  allTomasMap.set(point.tomaId, {
                    fecha: point.fecha,
                    tomaId: point.tomaId,
                    tableName: point.tableName
                  });
                }
              });
            }
          } catch (error: any) {
            console.error('Error loading price history for product', productId, error);
            
            // Si es error de "Too many connections", reintentar después de un delay
            if (error.response?.data?.details?.includes('Too many connections')) {
              
              // Mostrar mensaje al usuario
              const errorToast = document.createElement('div');
              errorToast.className = 'fixed top-4 right-4 bg-yellow-500 text-white px-4 py-2 rounded shadow-lg z-50';
              errorToast.textContent = 'Reconectando base de datos, por favor espere...';
              document.body.appendChild(errorToast);
              
              // Esperar 3 segundos y reintentar
              await new Promise(resolve => setTimeout(resolve, 3000));
              
              try {
                const retryResponse = await api.get(`/api/product/${encodeURIComponent(productId)}/price-history`);
                const historyData = retryResponse.data.history || retryResponse.data;
                const history: PriceHistoryPoint[] = Array.isArray(historyData) ? historyData : [];
                
                if (history.length > 0) {
                  allHistories[originalProductId] = history;
                  
                  history.forEach(point => {
                    if (!allTomasMap.has(point.tomaId)) {
                      allTomasMap.set(point.tomaId, {
                        fecha: point.fecha,
                        tomaId: point.tomaId,
                        tableName: point.tableName
                      });
                    }
                  });
                }
                
                // Remover el mensaje de error
                document.body.removeChild(errorToast);
              } catch (retryError) {
                console.error('Error en reintento para producto', productId, retryError);
                // Remover el mensaje de error
                if (document.body.contains(errorToast)) {
                  document.body.removeChild(errorToast);
                }
              }
            }
          }
        }
      }

      // Sort tomas by date
      const sortedTomas = Array.from(allTomasMap.values())
        .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());

      setPriceData(allHistories);
      setTomas(sortedTomas);
      setHasLoadedData(true);

    } catch (error) {
      console.error('Error in loadPriceHistory:', error);
    } finally {
      setLocalLoading(false);
    }
  };

  // Efecto para procesar datos precargados
  useEffect(() => {
    if (priceHistoryData && priceHistoryData.productHistories && priceHistoryData.tomas) {
      setPriceData(priceHistoryData.productHistories);
      setTomas(priceHistoryData.tomas);
      setHasLoadedData(true);
    }
  }, [priceHistoryData, comparisonProducts]);
  
  useEffect(() => {
    // Solo cargar si shouldLoad es true y no hay datos precargados
    if (shouldLoad && !priceHistoryData) {
      loadPriceHistory();
    }
  }, [comparisonProducts, shouldLoad, priceHistoryData]);

  // Format date for display - day + abbreviated month
  const formatDate = (dateString: string) => {
    try {
      // Parse date correctly to avoid timezone issues
      let date: Date;
      if (!dateString.includes('T')) {
        // Use UTC to avoid timezone issues completely
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const [year, month, day] = parts.map(Number);
          date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
        } else {
          return dateString;
        }
      } else {
        date = new Date(dateString);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return dateString; // Return original string if parsing fails
      }

      // Get current language from i18n
      const currentLang = i18n.language;

      // Use UTC methods to avoid timezone issues
      const day = date.getUTCDate();
      const monthNames: {[key: string]: string[]} = {
        'es': ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
        'en': ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        'pt': ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'],
        'it': ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic']
      };

      const months = monthNames[currentLang] || monthNames['en'];
      const month = months[date.getUTCMonth()];

      return `${day} ${month}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Get price data for a specific product and toma
  const getPriceForProductAndToma = (productId: string, tomaId: number, previousTomaId?: number) => {
    const productHistory = priceData[productId];
    if (!productHistory) return null;

    const pricePoint = productHistory.find((p: any) => p.tomaId === tomaId);
    if (!pricePoint) return null;

    // The actual product data might be nested in different ways
    const data = pricePoint.data || pricePoint;

    // Extract site - check both nested data and top level
    const site = data.sitio || data.SITIO || data.site || data.SITE ||
                 (pricePoint as any).sitio || (pricePoint as any).site || '';

    // Extract prices - Always use sale_price as the displayed price
    const salePriceRaw = data.sale_price || data.SALE_PRICE || data.precio_venta || data.PRECIO_VENTA || 
                         data.precio || data.PRECIO || data.price || data.PRICE || data.precioVenta;
    const originalPriceRaw = data.original_price || data.ORIGINAL_PRICE || data.precio_original || 
                            data.PRECIO_ORIGINAL || data.precioOriginal || data.precio_lista || data.PRECIO_LISTA;
    const discountedPriceRaw = data.discounted_price || data.DISCOUNTED_PRICE || data.precio_desc || 
                               data.PRECIO_DESC || data.precio_descuento || data.PRECIO_DESCUENTO || data.precioDesc;
    
    // Los campos son DECIMAL(10,2) - los valores ya están en pesos correctamente
    // No hay que multiplicar ni dividir por nada
    const parsePrice = (price: any) => {
      if (!price) return null;
      const parsed = typeof price === 'number' ? price : parseFloat(String(price));
      return isNaN(parsed) ? null : parsed;
    };
    
    // Los valores ya vienen correctamente de la BD en formato DECIMAL(10,2)
    // No hay necesidad de ajustar
    const salePrice = parsePrice(salePriceRaw);
    const originalPrice = parsePrice(originalPriceRaw);

    // No hacer ningún ajuste - los precios ya están correctos
    const finalSalePrice = salePrice;
    const finalOriginalPrice = originalPrice;
    
    // Determine if there's a discount - check if original_price > sale_price
    const hasDiscount = finalOriginalPrice && finalSalePrice && finalOriginalPrice > finalSalePrice;
    const discountValue = hasDiscount ? (finalOriginalPrice - finalSalePrice) : 0;
    const discountPercentage = hasDiscount ? ((finalOriginalPrice - finalSalePrice) / finalOriginalPrice * 100) : 0;
    
    
    // Calculate price change if we have a previous toma
    let priceChange = null;
    let priceChangePercentage = null;
    
    if (previousTomaId && finalSalePrice) {
      const previousPoint = productHistory.find((p: any) => p.tomaId === previousTomaId);
      if (previousPoint) {
        const previousData = previousPoint.data || previousPoint;
        const previousSalePriceRaw = previousData.sale_price || previousData.SALE_PRICE || 
                                    previousData.precio_venta || previousData.PRECIO_VENTA || 
                                    previousData.precio || previousData.PRECIO || 
                                    previousData.price || previousData.PRICE || previousData.precioVenta;
        const previousSalePrice = parsePrice(previousSalePriceRaw);
        
        if (previousSalePrice) {
          priceChange = finalSalePrice - previousSalePrice;
          priceChangePercentage = ((finalSalePrice - previousSalePrice) / previousSalePrice) * 100;
        }
      }
    }
    
    return {
      finalPrice: finalSalePrice, // Always show sale_price (adjusted)
      originalPrice: finalOriginalPrice,
      hasDiscount,
      discountValue: discountValue,
      discountPercentage: discountPercentage,
      priceChange,
      priceChangePercentage,
      site
    };
  };

  // Unified price tooltip component
  const PriceTooltip = ({ productId, tomaId, cellRef }: { productId: string, tomaId: number, cellRef: React.RefObject<HTMLTableCellElement> }) => {
    const tomaIndex = tomas.findIndex(t => t.tomaId === tomaId);
    const previousTomaId = tomaIndex > 0 ? tomas[tomaIndex - 1].tomaId : undefined;
    const priceInfo = getPriceForProductAndToma(productId, tomaId, previousTomaId);
    
    if (!priceInfo || (!priceInfo.hasDiscount && (!priceInfo.priceChange || priceInfo.priceChange === 0))) return null;

    // Calculate position based on cell reference
    const cellRect = cellRef.current?.getBoundingClientRect();
    if (!cellRect) return null;

    const tooltipStyle = {
      position: 'fixed' as const,
      left: cellRect.left + cellRect.width / 2,
      top: cellRect.top - 8,
      transform: 'translate(-50%, -100%)',
      zIndex: 9999,
      pointerEvents: 'none' as const
    };

    const tooltipContent = (
      <div 
        className="bg-white p-3 border border-gray-300 rounded-lg shadow-2xl"
        style={{ 
          ...tooltipStyle,
          minWidth: '220px',
          maxWidth: '300px',
          backgroundColor: 'white',
          isolation: 'isolate',
          fontSize: '14px', // Ensure consistent font size
          lineHeight: '1.5' // Ensure consistent line height
        }}
      >
        <div className="space-y-2">
          {/* Price change section */}
          {priceInfo.priceChange && priceInfo.priceChange !== 0 && (
            <div className="space-y-1">
              <div className="text-xs font-semibold text-gray-700 border-b pb-1 mb-1">
                {t('compare.price_change.title')}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t('compare.price_change.variation')}:</span>
                <span className={`text-sm font-medium ${priceInfo.priceChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {priceInfo.priceChange > 0 ? '+' : ''}${priceInfo.priceChange ? priceInfo.priceChange.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t('compare.price_change.percentage')}:</span>
                <span className={`text-sm font-medium ${priceInfo.priceChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {priceInfo.priceChange > 0 ? '+' : ''}{priceInfo.priceChangePercentage?.toFixed(1)}%
                </span>
              </div>
            </div>
          )}
          
          {/* Discount section */}
          {priceInfo.hasDiscount && (
            <>
              {priceInfo.priceChange && priceInfo.priceChange !== 0 && (
                <div className="border-t pt-2"></div>
              )}
              <div className="space-y-1">
                <div className="text-xs font-semibold text-gray-700 border-b pb-1 mb-1">
                  {t('compare.discount_tooltip.title')}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('compare.discount_tooltip.original_price')}:</span>
                  <span className="text-sm font-medium text-gray-500" style={{ textDecoration: 'line-through', textDecorationStyle: 'dotted' }}>
                    ${priceInfo.originalPrice ? priceInfo.originalPrice.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('compare.discount_tooltip.discount_value')}:</span>
                  <span className="text-sm font-medium text-red-600">
                    -${priceInfo.discountValue ? priceInfo.discountValue.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('compare.discount_tooltip.discount_percentage')}:</span>
                  <span className="text-sm font-medium text-green-600">
                    {priceInfo.discountPercentage?.toFixed(1)}%
                  </span>
                </div>
              </div>
            </>
          )}
          
        </div>
        
        {/* Arrow pointing down */}
        <div 
          className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full"
          style={{
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: '8px 8px 0 8px',
            borderColor: 'white transparent transparent transparent',
            filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))'
          }}
        />
      </div>
    );

    return ReactDOM.createPortal(tooltipContent, document.body);
  };

  const handleExportToExcel = () => {
    if (comparisonProducts.length === 0 || tomas.length === 0 || Object.keys(priceData).length === 0) return;

    const summaryHeaderRow = [
      t('compare.table.product'),
      t('compare.export_columns.site'),
      ...tomas.map(toma => {
        const formattedDate = formatDate(toma.fecha);
        return formattedDate;
      })
    ];

    // PT-#63: en el pivot (Evolution) las fechas son headers por-toma → semana ISO 8601 (`AAAA-Www`) como sub-fila rotulada.
    const summaryWeekRow: (string | number)[] = ['Semana', '', ...tomas.map(toma => getSemanaIso(toma.fecha))];
    const summaryData: (string | number)[][] = [summaryHeaderRow, summaryWeekRow];
    const detailHeaderRow = [
      t('compare.export_columns.product'),
      t('compare.export_columns.brand'),
      t('compare.export_columns.site'),
      t('compare.export_columns.date'),
      'Semana',
      t('compare.export_columns.sale_price'),
      t('compare.export_columns.original_price'),
      t('compare.export_columns.discount_value'),
      t('compare.export_columns.discount_percentage'),
      t('compare.export_columns.variation'),
      t('compare.export_columns.variation_percentage')
    ];
    const detailData: (string | number)[][] = [detailHeaderRow];

    const formatNumber = (value?: number | null) => {
      if (value === null || value === undefined) return '';
      return Number(value.toFixed(2));
    };

    comparisonProducts.forEach(product => {
      // IMPORTANTE: Usar id_catalogo real (no comparisonId que tiene timestamp)
      const rawProductId = product.id_catalogo || product.ID_CATALOGO || product.id;
      const productId = rawProductId ? String(rawProductId) : product.comparisonId;
      const productTitle = product.titulo || product.title || product.TITULO || t('compare.table.no_title');
      const productBrand = product.Marca || product.marca || product.MARCA || t('compare.table.no_brand');
      const productSite = product.sitio || product.site || product.SITIO || '';
      const productLabel = `${productBrand} - ${productTitle}`;

      const summaryRow: (string | number)[] = [productLabel, productSite];

      tomas.forEach((toma, index) => {
        const previousTomaId = index > 0 ? tomas[index - 1].tomaId : undefined;
        const priceInfo = getPriceForProductAndToma(productId, toma.tomaId, previousTomaId);
        if (priceInfo && typeof priceInfo.finalPrice === 'number' && !isNaN(priceInfo.finalPrice)) {
          summaryRow.push(Number(priceInfo.finalPrice.toFixed(2)));

          detailData.push([
            productTitle,
            productBrand,
            priceInfo.site || '',
            formatDate(toma.fecha),
            getSemanaIso(toma.fecha),
            formatNumber(priceInfo.finalPrice),
            formatNumber(priceInfo.originalPrice),
            priceInfo.hasDiscount ? formatNumber(priceInfo.discountValue) : '',
            priceInfo.hasDiscount ? formatNumber(priceInfo.discountPercentage) : '',
            priceInfo.priceChange !== null ? formatNumber(priceInfo.priceChange) : '',
            priceInfo.priceChangePercentage !== null ? formatNumber(priceInfo.priceChangePercentage) : ''
          ]);
        } else {
          summaryRow.push('');
        }
      });

      summaryData.push(summaryRow);
    });

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = summaryHeaderRow.map((_, index) => ({
      wch: index === 0 ? 50 : index === 1 ? 25 : 18  // Producto: 50, Sitio: 25, Fechas: 18
    }));

    const detailSheet = XLSX.utils.aoa_to_sheet(detailData);
    detailSheet['!cols'] = [
      { wch: 40 },  // Producto
      { wch: 20 },  // Marca
      { wch: 20 },  // Sitio
      { wch: 15 },  // Fecha
      { wch: 12 },  // Semana (ISO AAAA-Www)
      { wch: 15 },  // Precio venta
      { wch: 15 },  // Precio original
      { wch: 18 },  // Valor descontado
      { wch: 18 },  // % descuento
      { wch: 15 },  // Variación
      { wch: 20 }   // % variación
    ];

    const workbook = XLSX.utils.book_new();
    const sanitizeSheetName = (name: string, fallback: string) => {
      if (!name) return fallback;
      const sanitized = name.replace(/[\\/?*[\]:]/g, '').trim();
      if (!sanitized) return fallback;
      return sanitized.substring(0, 31);
    };
    const summarySheetName = sanitizeSheetName(t('compare.export_sheet_matrix'), 'Evolution');
    const detailsSheetName = sanitizeSheetName(t('compare.export_sheet_details'), 'Details');
    XLSX.utils.book_append_sheet(workbook, summarySheet, summarySheetName);
    XLSX.utils.book_append_sheet(workbook, detailSheet, detailsSheetName);

    const today = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `price_evolution_${today}.xlsx`);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <RefreshCw className="h-6 w-6 text-blue-500 animate-spin mr-3" />
          <span className="text-gray-600">{t('compare.loading_price_history')}</span>
        </div>
      </div>
    );
  }

  if (Object.keys(priceData).length === 0 || tomas.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="text-gray-500">
          <h3 className="text-lg font-medium text-gray-800 mb-2">
            {t('compare.no_price_history')}
          </h3>
          <p className="text-gray-600">
            {t('compare.no_price_history_help')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative z-0">
      <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
            <TableIcon className="h-5 w-5 mr-2 text-blue-600" />
            {t('compare.analysis_matrix')}
          </h3>
          <p className="text-sm text-gray-600">
            {t('compare.analysis_matrix_subtitle')}
          </p>
        </div>
        <button
          onClick={handleExportToExcel}
          disabled={comparisonProducts.length === 0 || tomas.length === 0 || Object.keys(priceData).length === 0}
          className="inline-flex items-center space-x-3 px-4 py-2 border border-green-300 text-green-600 bg-white rounded-lg hover:bg-green-50 hover:border-green-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <FileSpreadsheet className="w-5 h-5 text-green-500" strokeWidth={1.5} />
          <span className="text-sm font-semibold tracking-[0.2em] text-green-600">XLS</span>
          <span className="text-sm text-green-500 font-medium">
            {t('compare.export_price_evolution').replace(/\s*\(.*\)\s*$/, '')}
          </span>
        </button>
      </div>

      <div className="overflow-x-auto relative">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                {t('compare.table.product')}
              </th>
              {tomas.map((toma) => (
                <th key={toma.tomaId} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  <span>{formatDate(toma.fecha)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {comparisonProducts.map((product, index) => {
              // IMPORTANTE: Usar id_catalogo real (no comparisonId que tiene timestamp) y convertir a String
              const rawProductId = product.id_catalogo || product.ID_CATALOGO || product.id;
              const productId = rawProductId ? String(rawProductId) : product.comparisonId;
              const productImage = product.imagen || product.image || product.IMAGEN || product.Imagen || product.IMAGE || '';
              const productTitle = product.titulo || product.title || product.TITULO || t('compare.table.no_title');
              const productBrand = product.Marca || product.marca || product.MARCA || t('compare.table.no_brand');
              const productSite = product.sitio || product.site || product.SITIO || '';

              return (
                <tr key={productId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-4 whitespace-nowrap sticky left-0 bg-inherit z-10">
                    <div className="flex items-center space-x-3">
                      {productImage && (
                        <img
                          src={productImage}
                          alt={productTitle}
                          className="w-10 h-10 object-cover rounded border border-gray-200"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{productBrand}</p>
                        <p className="text-xs text-gray-500 truncate" title={productTitle}>
                          {productTitle.length > 30 ? productTitle.substring(0, 30) + '...' : productTitle}
                        </p>
                        {productSite && (
                          <p className="text-xs text-blue-600 truncate">{productSite}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  {tomas.map((toma, tomaIndex) => {
                    const previousTomaId = tomaIndex > 0 ? tomas[tomaIndex - 1].tomaId : undefined;
                    const priceInfo = getPriceForProductAndToma(productId, toma.tomaId, previousTomaId);
                    const isHovered = hoveredCell?.productId === productId && hoveredCell?.tomaId === toma.tomaId;
                    const cellKey = `${productId}-${toma.tomaId}`;
                    
                    // Create ref if it doesn't exist
                    if (!cellRefs.current[cellKey]) {
                      cellRefs.current[cellKey] = React.createRef<HTMLTableCellElement>();
                    }
                    
                    return (
                      <td 
                        key={toma.tomaId}
                        ref={cellRefs.current[cellKey]}
                        className={`px-4 py-4 text-center whitespace-nowrap relative ${
                          priceInfo?.hasDiscount ? 'bg-red-50 bg-opacity-10' : ''
                        }`}
                        style={{
                          backgroundColor: priceInfo?.hasDiscount ? 'rgba(239, 68, 68, 0.1)' : undefined,
                          position: 'relative',
                          overflow: 'visible' // Changed from 'hidden' to allow tooltip
                        }}
                        onMouseEnter={() => setHoveredCell({ productId, tomaId: toma.tomaId })}
                        onMouseLeave={() => setHoveredCell(null)}
                      >
                        {priceInfo ? (
                          <div className="flex items-center justify-center space-x-1">
                            <span className={`text-sm font-medium ${priceInfo.hasDiscount ? 'text-red-600' : 'text-gray-900'}`}>
                              ${priceInfo.finalPrice ? Math.round(priceInfo.finalPrice).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '-'}
                            </span>
                            {/* Price change indicator */}
                            {priceInfo.priceChange !== null && priceInfo.priceChange !== 0 && (
                              priceInfo.priceChange > 0 ? (
                                <span className="text-xs" style={{ filter: 'hue-rotate(120deg)' }}>🔺</span>
                              ) : (
                                <span className="text-xs">🔻</span>
                              )
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                        {/* Render tooltip outside of the cell content */}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Render tooltip conditionally */}
      {hoveredCell && (() => {
        const { productId, tomaId } = hoveredCell;
        const tomaIndex = tomas.findIndex(t => t.tomaId === tomaId);
        const previousTomaId = tomaIndex > 0 ? tomas[tomaIndex - 1].tomaId : undefined;
        const priceInfo = getPriceForProductAndToma(productId, tomaId, previousTomaId);
        const cellKey = `${productId}-${tomaId}`;
        
        if (priceInfo && (priceInfo.hasDiscount || (priceInfo.priceChange && priceInfo.priceChange !== 0))) {
          return (
            <PriceTooltip 
              productId={productId} 
              tomaId={tomaId} 
              cellRef={cellRefs.current[cellKey]}
            />
          );
        }
        return null;
      })()}
    </div>
  );
};

export default AnalysisMatrix;
